import React, { FC, useState, useRef } from 'react'
import { motion, useScroll, useTransform, useInView } from 'framer-motion'

interface ReliabilityServiceStepsProps {
    customService: any
}

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.3
        }
    }
}

const iconVariants = {
    initial: { scale: 1 },
    hover: { 
        scale: 1.1, 
        rotate: 5,
        transition: { 
            type: "spring",
            stiffness: 400,
            damping: 10
        }
    }
}

const ReliabilityServiceSteps: FC<ReliabilityServiceStepsProps> = ({ customService }) => {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const { scrollYProgress } = useScroll();
    const backgroundOpacity = useTransform(scrollYProgress, [0, 0.5], [0.8, 1]);
    
    // References for scroll-triggered animations
    const sectionRef = useRef(null);
    const gridRef = useRef(null);
    const imageRef = useRef(null);
    
    // InView hooks to detect when elements enter viewport
    const sectionInView = useInView(sectionRef, { once: false, amount: 0.2 });
    const gridInView = useInView(gridRef, { once: false, amount: 0.1 });
    const imageInView = useInView(imageRef, { once: false, amount: 0.1 });
    
    return (
        <motion.div 
            ref={sectionRef}
            initial={{ opacity: 0 }}
            animate={sectionInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.8 }}
            style={{ background: `rgba(18, 18, 18, ${backgroundOpacity})` }}
            className='flex flex-col md:flex-row px-0 md:px-8 lg:px-16 xl:px-32 py-6 gap-4 lg:gap-16 bg-blackDark relative'
        >
            {/* Animated background elements */}
            <motion.div 
                className="absolute top-10 left-10 w-20 h-20 rounded-full bg-opacity-10 bg-blue-500 filter blur-xl"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={sectionInView ? { 
                    opacity: 0.6,
                    x: [0, 30, 0],
                    y: [0, 20, 0],
                    scale: [1, 1.2, 1],
                    rotate: [0, 90, 0]
                } : { opacity: 0, scale: 0.5 }}
                transition={{ 
                    duration: 15, 
                    repeat: Infinity,
                    repeatType: "reverse",
                    opacity: { duration: 1, delay: 0.3 }
                }}
            />
            <motion.div 
                className="absolute bottom-40 right-40 w-32 h-32 rounded-full bg-opacity-10 bg-purple-500 filter blur-xl hidden sm:block"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={sectionInView ? { 
                    opacity: 0.6,
                    x: [0, -40, 0],
                    y: [0, 30, 0],
                    scale: [1, 1.3, 1],
                } : { opacity: 0, scale: 0.5 }}
                transition={{ 
                    duration: 20, 
                    repeat: Infinity,
                    repeatType: "reverse",
                    opacity: { duration: 1, delay: 0.5 }
                }}
            />

            <motion.div 
                ref={gridRef}
                variants={containerVariants}
                initial="hidden"
                animate={gridInView ? "visible" : "hidden"}
                className='grid grid-cols-2 w-full md:w-[60%] first:col-span-1 relative'
            >
                {customService.ReliabilityServiceData.map((reliable: any, index: number) => {
                    // Calculate delay based on index for sequential animations on scroll
                    const delay = index * 0.15;
                    
                    return (
                        <motion.div 
                            key={reliable.key}
                            custom={index}
                            variants={{
                                hidden: { opacity: 0, y: 20 },
                                visible: i => ({
                                    opacity: 1,
                                    y: 0,
                                    transition: { 
                                        duration: 0.5,
                                        delay: i * 0.15 
                                    }
                                })
                            }}
                            whileHover="hover"
                            onHoverStart={() => setHoveredIndex(index)}
                            onHoverEnd={() => setHoveredIndex(null)}
                            className={`flex flex-col gap-4 rounded-3xl p-4 lg:p-6 cursor-pointer`} 
                        >
                            <motion.div 
                                variants={iconVariants}
                                whileHover="hover"
                                className={`w-8 h-8 md:w-14 md:h-14 rounded-full bg-${reliable.color} text-black flex items-center justify-center font-bold text-base md:text-2xl relative z-10`}
                            >
                                {reliable.key}.
                                <motion.div 
                                    className="absolute inset-0 rounded-full bg-white opacity-20"
                                    initial={{ scale: 0 }}
                                    animate={hoveredIndex === index ? { scale: 1.5, opacity: 0 } : { scale: 0 }}
                                    transition={{ duration: 0.8, repeat: hoveredIndex === index ? Infinity : 0 }}
                                />
                            </motion.div>
                            <div className='flex flex-col'>
                                <motion.div 
                                    variants={{
                                        hidden: { opacity: 0, y: 5 },
                                        visible: { 
                                            opacity: 1, 
                                            y: 0,
                                            transition: { duration: 0.3, delay: delay + 0.1 } 
                                        }
                                    }}
                                    className='font-semibold text-lg sm:text-xl'
                                >
                                    {reliable.title}
                                </motion.div>
                                <motion.div 
                                    variants={{
                                        hidden: { opacity: 0, y: 5 },
                                        visible: { 
                                            opacity: 1, 
                                            y: 0,
                                            transition: { duration: 0.3, delay: delay + 0.2 } 
                                        }
                                    }}
                                    className='text-sm sm:text-md pt-3 pr-0 lg:pr-8'
                                >
                                    {reliable.subtitle}
                                </motion.div>
                            </div>
                        </motion.div>
                    )
                })}
            </motion.div>

            <motion.div 
                ref={imageRef}
                initial={{ opacity: 0, x: 50 }}
                animate={imageInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                whileHover={{ scale: 1.03 }}
                className='hidden md:block bg-pretty-woman-bg bg-no-repeat bg-contain md:bg-origin-content h-64 sm:h-80 md:h-auto w-full md:w-[40%] relative mt-4 md:mt-0' 
            >
                <motion.div 
                    className="absolute inset-0 bg-gradient-to-t from-blackDark via-transparent to-transparent"
                    initial={{ opacity: 0 }}
                    animate={imageInView ? { opacity: 0.4 } : { opacity: 0 }}
                    transition={{ duration: 1, delay: 1 }}
                />
            </motion.div>
        </motion.div>
    )
}

export default ReliabilityServiceSteps
